import { InfiniteData } from '@tanstack/react-query';

import { queryClient } from '../components/queryClient';
import { Event } from '../interfaces';
import { deepCopy } from '../utils/deepCopy';

/**
 *
 * @param event Event to put into cache
 *
 * @description This function is used to put an event into the query cache,
 * and to invalidate the query, so that the next time the event is requested (via hook)
 * it will be fetched from the server BUT before the server responds, the cached data
 * will be displayed (instantenously) so that the app will feel snappier.
 */
export const setEventQueryCache = (event: Event) => {
  // Put data (or partial data) into cache
  queryClient.setQueryData(['event', event.id], event);

  // in case this event exists in any of the infinite queries (useEventsQuery)
  // find that element in the list by ID and update it
  updateInfiniteEvent(event);

  // Make sure we refetch the event in the background, WHEN NEEDED - but display
  // the cached data in the meantime, instantenously
  queryClient.invalidateQueries(['event', event.id]);
};

export const invalidateEvent = async (eventId: number) => {
  queryClient.invalidateQueries({ queryKey: ['event', eventId], refetchType: 'all' });
};

export const updateInfiniteEvent = (event: Event) => {
  queryClient.setQueriesData<InfiniteData<Event[]> | undefined>(
    { queryKey: ['events'], type: 'active' },
    (originalData) => {
      const data = deepCopy(originalData);
      if (data) {
        // eslint-disable-next-line no-param-reassign
        data.pages = (data.pages || []).map((page) => {
          // eslint-disable-next-line no-param-reassign
          page.events = (page.events || []).map((currentEvent) => {
            // @ts-ignore
            if (currentEvent.id === event.id) {
              const newEvent = deepCopy(event);
              newEvent.updated_at = new Date().toISOString();
              if (newEvent.tags === undefined) {
                newEvent.tags = currentEvent.tags;
              }
              return newEvent;
            }
            return currentEvent;
          });
          return page;
        });
        return data;
      }
      return originalData;
    },
  );
};

// These two queries are used to navigate the list of portal list events from the viewer modal,
// (like when using arrow keys) -
export const setEventsListEvents = (events: Event[]) => {
  queryClient.setQueryData(['eventsListEvents'], events);
};

export const setExpirationDateForGroups = (timeString: string) => {
  const moment = require('moment-timezone');
  const date = moment(timeString);
  queryClient.setQueryData(['expirationDateForGroups'], date);
};
